import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useAuth } from '../auth/AuthContext'

// useLiveUpdates opens a WebSocket to /api/v1/ws and uses each message as
// a React-Query cache-bust signal. The dashboard still reads canonical
// state via REST — the socket just tells it *when* to refetch.
//
// Reconnects with exponential backoff (1s → 2s → 4s → 8s, capped 30s).
// Token is passed on the URL (?token=…) because browsers can't set an
// Authorization header on the WebSocket upgrade request.

type Status = 'idle' | 'connecting' | 'open' | 'closed'

export type LiveMessage = {
  subject: string
  kind: string
  tenant: string
  body?: unknown
  publish_at: string
}

export function useLiveUpdates(): { status: Status; lastMessage: LiveMessage | null } {
  const { accessToken } = useAuth()
  const qc = useQueryClient()
  const [status, setStatus] = useState<Status>('idle')
  const [lastMessage, setLastMessage] = useState<LiveMessage | null>(null)
  const backoffRef = useRef(1000)
  const wsRef = useRef<WebSocket | null>(null)

  useEffect(() => {
    if (!accessToken) return
    let cancelled = false

    const connect = () => {
      if (cancelled) return
      setStatus('connecting')
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
      const url = `${scheme}://${window.location.host}/api/v1/ws?token=${encodeURIComponent(accessToken)}`
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        setStatus('open')
        backoffRef.current = 1000
      }
      ws.onclose = () => {
        wsRef.current = null
        setStatus('closed')
        if (cancelled) return
        const delay = Math.min(backoffRef.current, 30_000)
        backoffRef.current = Math.min(backoffRef.current * 2, 30_000)
        setTimeout(connect, delay)
      }
      ws.onerror = () => {
        // onclose always fires after onerror — leave reconnect to that.
      }
      ws.onmessage = (evt) => {
        let msg: LiveMessage
        try {
          msg = JSON.parse(evt.data as string) as LiveMessage
        } catch {
          return
        }
        setLastMessage(msg)
        dispatchInvalidation(qc, msg.kind)
      }
    }

    connect()
    return () => {
      cancelled = true
      wsRef.current?.close()
    }
  }, [accessToken, qc])

  return { status, lastMessage }
}

function dispatchInvalidation(
  qc: ReturnType<typeof useQueryClient>,
  kind: string,
): void {
  switch (kind) {
    case 'exception.raised':
    case 'exception.updated':
    case 'exception.resolved':
      void qc.invalidateQueries({ queryKey: ['exceptions'] })
      void qc.invalidateQueries({ queryKey: ['stats'] })
      // `['exception', id]` keys are fetched on open-drawer; let the
      // drawer's own mutation hooks keep them fresh. Invalidating the
      // whole prefix would cause every previously-opened detail to refetch.
      break
    case 'engineer.state_changed':
      void qc.invalidateQueries({ queryKey: ['engineers'] })
      void qc.invalidateQueries({ queryKey: ['stats'] })
      break
    case 'ws.ready':
      // Just a hello frame.
      break
    default:
      break
  }
}
